import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Dropdown} from "primereact/dropdown";
import {Message} from "primereact/message";
import {Password} from "primereact/password";
import {ProgressSpinner} from "primereact/progressspinner";
import {TabPanel, TabView} from "primereact/tabview";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import MarkdownHelp from "../../system/components/markdown-help";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../system/admin-access";
import {
    createAdminEntity,
    fetchAdminEntity,
    fetchAllAdminCollection,
    updateAdminEntity,
} from "../../system/content-type-admin-api";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {useTranslate} from "../../system/useTranslate";
import {parseDate} from "../../system/utils";
import {entityId, entityLabel, formatMoney} from "../operations/operations-utils";
import {fetchUserSessions, recoverUserAccess, revokeUserSession} from "../../system/support-access-api";

function errorText(error) {
    return error?.response?.data?.error?.message
        || error?.response?.data?.error
        || error?.message
        || "user-load-error";
}

function dateText(value) {
    return value ? parseDate(value, "iso", "YYYY/MM/DD HH:mm", "gregorian", "en") : "-";
}

function relationMatches(value, user) {
    const id = String(entityId(value) || "");
    return id && [user?.id, user?.documentId].map(String).includes(id);
}

function statusSeverity(value) {
    if (["joined", "succeeded", "completed", "active"].includes(value)) return "success";
    if (["removed", "failed", "blocked"].includes(value)) return "danger";
    return "warning";
}

function ReadonlyField({label, value}) {
    const translate = useTranslate();
    return (
        <div className="p-3 border-1 border-200 border-round surface-card flex flex-column gap-1 h-full">
            <span className="text-xs text-500">{translate(label)}</span>
            <span className="text-sm font-medium ltr">{value ?? "-"}</span>
        </div>
    );
}

function User() {
    const {documentId} = useParams();
    const navigate = useNavigate();
    const translate = useTranslate();
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({});
    const [memberships, setMemberships] = useState([]);
    const [roles, setRoles] = useState([]);
    const [events, setEvents] = useState([]);
    const [payments, setPayments] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [noteDialog, setNoteDialog] = useState(false);
    const [note, setNote] = useState("");
    const [securityDialog, setSecurityDialog] = useState(null);
    const [securityForm, setSecurityForm] = useState({verificationNote: "", phoneVerified: false, newPassword: "", keepSessionDocumentId: ""});
    const adminUser = useSelector((state) => state.auth.user);
    const can = useCallback(
        (permission) => hasAdminPermission(adminUser, permission),
        [adminUser]
    );

    const loadUser = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const nextUser = await fetchAdminEntity(CONTENT_TYPE_UIDS.USER, documentId);
            const results = await Promise.allSettled([
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.USER_ROLE, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.USER_EVENT_LOG, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.PAYMENT, {pageSize: 200}),
                fetchAllAdminCollection("api::workspace-invitation.workspace-invitation", {pageSize: 200}),
                fetchAllAdminCollection("api::audit-log.audit-log", {pageSize: 200}),
            ]);
            const items = (index) => results[index].status === "fulfilled"
                ? results[index].value.items
                : [];
            const nextMemberships = items(0).filter((item) => relationMatches(item.member, nextUser));
            const membershipIds = new Set(nextMemberships.map((item) => String(entityId(item))));

            setUser(nextUser);
            setForm({
                username: nextUser?.username || "",
                firstname: nextUser?.firstname || "",
                lastname: nextUser?.lastname || "",
                fullname: nextUser?.fullname || "",
                email: nextUser?.email || "",
                phonenumber: nextUser?.phonenumber || "",
                provider: nextUser?.provider || "",
                jobPosition: nextUser?.jobPosition || "",
                about: nextUser?.about || "",
                confirmed: Boolean(nextUser?.confirmed),
                verified: Boolean(nextUser?.verified),
                blocked: Boolean(nextUser?.blocked),
                password: "",
            });
            setMemberships(nextMemberships);
            setRoles(items(1).filter((item) => membershipIds.has(String(entityId(item.workspace_membership)))));
            setEvents(items(2).filter((item) => relationMatches(item.owner, nextUser)));
            setPayments(items(3).filter((item) => membershipIds.has(String(entityId(item.user)))));
            setInvitations(items(4).filter((item) => relationMatches(item.invitee, nextUser)));
            setAuditLogs(items(5).filter((item) => (
                Array.isArray(item.relatedUserIds)
                && item.relatedUserIds.map(String).includes(String(nextUser?.id))
            )));
            if (can(ADMIN_PERMISSIONS.USER_SESSION_ACTIONS)) {
                setSessions(await fetchUserSessions(documentId));
            } else {
                setSessions([]);
            }
        } catch (requestError) {
            setError(errorText(requestError));
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [can, documentId]);

    const canViewUser = can(ADMIN_PERMISSIONS.USER_VIEW);

    useEffect(() => {
        if (canViewUser) loadUser();
    }, [canViewUser, loadUser]);

    const rolesByMembership = useMemo(() => {
        const grouped = new Map();
        roles.forEach((role) => {
            const id = String(entityId(role.workspace_membership) || "");
            if (!grouped.has(id)) grouped.set(id, []);
            grouped.get(id).push(role);
        });
        return grouped;
    }, [roles]);
    const membershipRows = memberships.map((membership) => {
        const membershipRoles = rolesByMembership.get(String(entityId(membership))) || [];
        return {
            ...membership,
            _workspace: entityLabel(membership.workspace),
            _workspaceId: entityId(membership.workspace),
            _roles: membershipRoles.map((role) => role.role).join(", ") || "-",
        };
    });
    const failedPayments = payments.filter((payment) => payment.paymentStatus === "failed").length;
    const pendingInvitations = invitations.filter((invitation) => (
        !["accepted", "canceled", "expired"].includes(invitation.invitationStatus)
    )).length;

    const setField = (field, value) => setForm((current) => ({...current, [field]: value}));
    const saveUser = async () => {
        if (!form.username.trim() || !form.email.trim()) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = {
                username: form.username.trim(),
                firstname: form.firstname.trim() || null,
                lastname: form.lastname.trim() || null,
                fullname: form.fullname.trim() || null,
                email: form.email.trim(),
                phonenumber: form.phonenumber.trim(),
                provider: form.provider.trim() || null,
                jobPosition: form.jobPosition.trim() || null,
                about: form.about.trim() || null,
                confirmed: form.confirmed,
                verified: form.verified,
                blocked: form.blocked,
            };
            const updated = await updateAdminEntity(CONTENT_TYPE_UIDS.USER, documentId, payload);
            setUser((current) => ({...current, ...updated}));
            setForm((current) => ({...current, password: ""}));
            setSuccess("user-updated-successfully");
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    const quickUpdate = async (patch, successKey) => {
        setSaving(true);
        setError(null);
        try {
            await updateAdminEntity(CONTENT_TYPE_UIDS.USER, documentId, patch);
            setForm((current) => ({...current, ...patch}));
            setUser((current) => ({...current, ...patch}));
            setSuccess(successKey);
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    const saveNote = async () => {
        if (note.trim().length < 3) return;
        setSaving(true);
        setError(null);
        try {
            await createAdminEntity(CONTENT_TYPE_UIDS.USER_EVENT_LOG, {
                owner: entityId(user),
                title: translate("user-internal-note-title"),
                message: note.trim(),
                displayType: "invisible",
                eventSurface: "activity",
                eventCategory: "system",
                eventKind: "admin.internal_support_note",
                sourceType: "admin-support",
                sourceDocumentId: String(entityId(user)),
                sourceLabel: entityLabel(user),
                priority: "normal",
                requiresResponse: false,
                payload: {internal: true},
            });
            setNote("");
            setNoteDialog(false);
            setSuccess("user-internal-note-saved");
            await loadUser();
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    const openSecurityDialog = (mode, session = null) => {
        setSecurityForm({
            verificationNote: "",
            phoneVerified: false,
            newPassword: "",
            keepSessionDocumentId: mode === "recover" ? (session?.id || "") : "",
        });
        setSecurityDialog({mode, session});
        setError(null);
        setSuccess(null);
    };

    const runSessionSecurityAction = async () => {
        if (!securityForm.phoneVerified || securityForm.verificationNote.trim().length < 10) return;
        if (securityDialog?.mode === "recover" && securityForm.newPassword.length < 8) return;
        setSaving(true);
        setError(null);
        try {
            if (securityDialog?.mode === "recover") {
                const result = await recoverUserAccess(documentId, {
                    phoneVerified: securityForm.phoneVerified,
                    verificationNote: securityForm.verificationNote.trim(),
                    newPassword: securityForm.newPassword,
                    keepSessionDocumentId: securityForm.keepSessionDocumentId || null,
                });
                setSessions(result?.sessions || []);
                setSuccess("user-access-recovered-successfully");
            } else {
                await revokeUserSession(
                    documentId,
                    securityDialog.session.id,
                    securityForm.verificationNote.trim(),
                    securityForm.phoneVerified
                );
                setSessions(await fetchUserSessions(documentId));
                setSuccess("user-session-revoked-successfully");
            }
            setSecurityDialog(null);
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    const activeSessionOptions = sessions
        .filter((session) => session.sessionStatus === "active")
        .map((session) => ({
            label: `${session.deviceName} · ${session.browser} · ${dateText(session.lastActivityAt)}`,
            value: session.id,
        }));

    if (loading && !user) {
        return <div className="w-full h-full flex align-items-center justify-content-center"><ProgressSpinner/></div>;
    }

    if (!canViewUser) {
        return <AdminAccessDenied/>;
    }

    return (
        <div className="w-full h-full overflow-auto flex flex-column gap-3 p-3 rtl text-xs">
            <PageHeaderButton
                title={translate("user-operations-title")}
                description={translate("user-operations-description")}
                end={(
                    <div className="flex gap-2">
                        <Button icon="pi pi-arrow-right" label={translate("back")} className="gap-2" outlined onClick={() => navigate("/users")}/>
                        <Button icon="pi pi-refresh" label={translate("refresh")} className="gap-2" outlined loading={loading} onClick={loadUser}/>
                    </div>
                )}
            />
            {error && <Message severity="error" text={translate(error)} className="text-xs"/>}
            {success && <Message severity="success" text={translate(success)} className="text-xs"/>}
            <div className="p-3 border-1 border-200 border-round surface-card flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="flex flex-column gap-1">
                    <span className="text-lg font-bold">{entityLabel(user)}</span>
                    <span className="text-xs text-500 ltr">{user?.email} · {user?.phonenumber}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Tag severity={form.blocked ? "danger" : "success"} value={translate(form.blocked ? "blocked" : "active")}/>
                    <Tag severity={form.confirmed ? "success" : "warning"} value={translate(form.confirmed ? "confirmed" : "unconfirmed")}/>
                    <Tag severity="info" value={`${translate("workspace-memberships")}: ${memberships.length}`}/>
                    {failedPayments > 0 && <Tag severity="danger" value={`${translate("operations-finance-failed-payments")}: ${failedPayments}`}/>}
                </div>
            </div>
            <TabView className="rtl text-xs">
                {can(ADMIN_PERMISSIONS.USER_TAB_PROFILE) && (
                <TabPanel header={translate("user-tab-overview")}>
                    <div className="flex flex-column gap-3">
                        <div className="grid">
                            {[
                                ["username", "username"],
                                ["firstname", "firstname"],
                                ["lastname", "lastname"],
                                ["fullname", "fullname"],
                                ["email", "email"],
                                ["phonenumber", "phonenumber"],
                                ["provider", "provider"],
                                ["jobPosition", "job-position"],
                            ].map(([field, label]) => (
                                <div key={field} className="col-12 md:col-6 lg:col-4 flex flex-column gap-2">
                                    <label className="font-bold" htmlFor={`user-${field}`}>{translate(label)}</label>
                                    <InputText disabled={!can(ADMIN_PERMISSIONS.USER_UPDATE)} id={`user-${field}`} className={["email", "phonenumber"].includes(field) ? "ltr" : ""} value={form[field] || ""} onChange={(event) => setField(field, event.target.value)}/>
                                </div>
                            ))}
                            <div className="col-12 flex flex-column gap-2">
                                <label className="font-bold" htmlFor="user-about">{translate("about")}</label>
                                <InputTextarea disabled={!can(ADMIN_PERMISSIONS.USER_UPDATE)} id="user-about" rows={4} value={form.about || ""} onChange={(event) => setField("about", event.target.value)}/>
                            </div>
                            <div className="col-12 md:col-6 lg:col-4">
                                <label className="p-3 border-1 border-200 border-round flex align-items-center justify-content-between gap-3 h-full">
                                    <span>{translate("confirmed")}</span>
                                    <Checkbox disabled={!can(ADMIN_PERMISSIONS.USER_UPDATE)} checked={Boolean(form.confirmed)} onChange={(event) => setField("confirmed", event.checked)}/>
                                </label>
                            </div>
                            <div className="col-12 md:col-6 lg:col-4">
                                <label className="p-3 border-1 border-200 border-round flex align-items-center justify-content-between gap-3 h-full">
                                    <span>{translate("verified")}</span>
                                    <Checkbox disabled={!can(ADMIN_PERMISSIONS.USER_UPDATE)} checked={Boolean(form.verified)} onChange={(event) => setField("verified", event.checked)}/>
                                </label>
                            </div>
                        </div>
                        {can(ADMIN_PERMISSIONS.USER_UPDATE) && <div className="flex justify-content-end">
                            <Button icon="pi pi-save" label={translate("save-changes")} className="gap-2" loading={saving} onClick={saveUser}/>
                        </div>}
                        <MarkdownHelp contentKey="user-tab-overview-help"/>
                    </div>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.USER_TAB_WORKSPACES) && (
                <TabPanel header={translate("user-tab-workspaces-memberships")}>
                    <DataTable value={membershipRows} dataKey="documentId" paginator rows={15} rowHover emptyMessage={translate("datatable-empty-message")}>
                        <Column className="text-right" field="_workspace" header={translate("workspace")}/>
                        <Column className="text-right" field="memberShipStatus" header={translate("membership-status")} body={(row) => <Tag severity={statusSeverity(row.memberShipStatus)} value={translate(row.memberShipStatus)}/>}/>
                        <Column className="text-right" field="_roles" header={translate("role")}/>
                        <Column className="text-right" field="isDefault" header={translate("default-workspace")} body={(row) => translate(row.isDefault ? "yes" : "no")}/>
                        <Column className="text-right ltr" header={translate("createdAt")} body={(row) => dateText(row.createdAt)}/>
                        {can(ADMIN_PERMISSIONS.WORKSPACE_VIEW) && <Column
                            className="text-right"
                            header={translate("actions")}
                            body={(row) => (
                                <Button icon="pi pi-external-link" label={translate("workspace-open-operations")} className="gap-2" text onClick={() => navigate(`/workspaces/${row._workspaceId}`)}/>
                            )}
                        />}
                    </DataTable>
                    <MarkdownHelp contentKey="user-tab-workspaces-memberships-help"/>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.USER_TAB_ACTIVITY) && (
                <TabPanel header={translate("user-tab-activity")}>
                    <DataTable value={events} dataKey="documentId" paginator rows={15} rowHover emptyMessage={translate("datatable-empty-message")}>
                        <Column className="text-right" field="title" header={translate("title")}/>
                        <Column className="text-right" field="message" header={translate("message")}/>
                        <Column className="text-right" field="eventKind" header={translate("event-kind")}/>
                        <Column className="text-right" header={translate("workspace")} body={(row) => entityLabel(row.workspace)}/>
                        <Column className="text-right" field="priority" header={translate("operations-priority")}/>
                        <Column className="text-right ltr" header={translate("createdAt")} body={(row) => dateText(row.createdAt)}/>
                    </DataTable>
                    <MarkdownHelp contentKey="user-tab-activity-help"/>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.USER_TAB_FINANCE) && (
                <TabPanel header={translate("user-tab-finance-invitations")}>
                    <div className="flex flex-column gap-3">
                        <div className="grid">
                            <div className="col-12 md:col-4"><ReadonlyField label="operations-total-payments" value={payments.length}/></div>
                            <div className="col-12 md:col-4"><ReadonlyField label="operations-finance-failed-payments" value={failedPayments}/></div>
                            <div className="col-12 md:col-4"><ReadonlyField label={translate("user-pending-invitations")} value={pendingInvitations}/></div>
                        </div>
                        <DataTable value={payments} dataKey="documentId" paginator rows={10} emptyMessage={translate("datatable-empty-message")}>
                            <Column className="text-right ltr" field="transactionNumber" header={translate("sales-transaction-number")}/>
                            <Column className="text-right" header={translate("workspace")} body={(row) => entityLabel(row.workspace)}/>
                            <Column className="text-right ltr" header={translate("sales-amount")} body={(row) => formatMoney(row.amount)}/>
                            <Column className="text-right" field="paymentStatus" header={translate("sales-status")} body={(row) => <Tag severity={statusSeverity(row.paymentStatus)} value={translate(`sales-status-${String(row.paymentStatus).replace(/_/g, "-")}`)}/>}/>
                            <Column className="text-right ltr" field="failureCode" header={translate("sales-failure-code")}/>
                        </DataTable>
                        <DataTable value={invitations} dataKey="documentId" paginator rows={10} emptyMessage={translate("datatable-empty-message")}>
                            <Column className="text-right" header={translate("workspace")} body={(row) => entityLabel(row.workspace)}/>
                            <Column className="text-right" field="invitationStatus" header={translate("status")}/>
                            <Column className="text-right ltr" field="email" header={translate("email")}/>
                            <Column className="text-right ltr" header={translate("createdAt")} body={(row) => dateText(row.createdAt)}/>
                        </DataTable>
                        <MarkdownHelp contentKey="user-tab-finance-invitations-help"/>
                    </div>
                </TabPanel>
                )}
                {can(ADMIN_PERMISSIONS.USER_TAB_SECURITY) && (
                <TabPanel header={translate("user-tab-security-support")}>
                    <div className="flex flex-column gap-3">
                        <Message severity="warn" text={translate("user-security-actions-description")} className="text-xs"/>
                        <div className="grid">
                            {can(ADMIN_PERMISSIONS.USER_OTP_CONTENT) && <div className="col-12 md:col-4"><ReadonlyField label="otp" value={user?.otpCode || "-"}/></div>}
                            {can(ADMIN_PERMISSIONS.USER_OTP_CONTENT) && <div className="col-12 md:col-4"><ReadonlyField label="otp-expires-at" value={dateText(user?.otpExpiresAt)}/></div>}
                            {can(ADMIN_PERMISSIONS.USER_OTP_CONTENT) && <div className="col-12 md:col-4"><ReadonlyField label={translate("user-otp-attempts")} value={user?.otpAttempts || 0}/></div>}
                            <div className="col-12 md:col-4"><ReadonlyField label="createdAt" value={dateText(user?.createdAt)}/></div>
                            <div className="col-12 md:col-4"><ReadonlyField label="updatedAt" value={dateText(user?.updatedAt)}/></div>
                            <div className="col-12 md:col-4"><ReadonlyField label="document-id" value={user?.documentId || user?.id}/></div>
                        </div>
                        {can(ADMIN_PERMISSIONS.USER_SECURITY_ACTIONS) && <div className="flex flex-wrap gap-2">
                            <Button
                                icon={form.blocked ? "pi pi-lock-open" : "pi pi-ban"}
                                label={translate(form.blocked ? "user-enable" : "user-disable")}
                                className="gap-2"
                                severity={form.blocked ? "success" : "danger"}
                                loading={saving}
                                onClick={() => quickUpdate({blocked: !form.blocked}, form.blocked ? "user-enabled-successfully" : "user-disabled-successfully")}
                            />
                            <Button icon="pi pi-check-circle" label={translate("user-confirm")} className="gap-2" outlined disabled={form.confirmed} onClick={() => quickUpdate({confirmed: true}, "user-confirmed-successfully")}/>
                            <Button icon="pi pi-refresh" label={translate("user-reset-otp-attempts")} className="gap-2" outlined onClick={() => quickUpdate({otpAttempts: 0, otpCode: null, otpExpiresAt: null}, "user-otp-reset-successfully")}/>
                        </div>}
                        {can(ADMIN_PERMISSIONS.USER_SESSION_ACTIONS) && (
                            <div className="surface-50 border-1 border-200 border-round p-3 flex flex-column gap-3">
                                <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-2">
                                    <div className="flex flex-column gap-1">
                                        <span className="font-bold text-sm">{translate("user-active-sessions")}</span>
                                        <span className="text-xs text-500">{translate("user-active-sessions-description")}</span>
                                    </div>
                                    {can(ADMIN_PERMISSIONS.USER_PASSWORD_RECOVERY) && (
                                        <Button icon="pi pi-shield" label={translate("user-recover-access")} className="gap-2" severity="danger" onClick={() => openSecurityDialog("recover")}/>
                                    )}
                                </div>
                                <DataTable value={sessions} dataKey="id" paginator rows={10} rowHover emptyMessage={translate("user-no-sessions")}>
                                    <Column className="text-right" header={translate("device")} body={(row) => <div className="flex flex-column gap-1"><span>{row.deviceName}</span><span className="text-xs text-500">{row.browser} · {row.operatingSystem}</span></div>}/>
                                    <Column className="text-right" header={translate("location")} body={(row) => row.approximateLocation || translate("location-unknown")}/>
                                    <Column className="text-right ltr" header={translate("ip-address")} body={(row) => row.ipAddress || "-"}/>
                                    <Column className="text-right ltr" header={translate("last-activity")} body={(row) => dateText(row.lastActivityAt)}/>
                                    <Column className="text-right" header={translate("status")} body={(row) => <Tag severity={statusSeverity(row.sessionStatus)} value={translate(`user-session-status-${row.sessionStatus}`)}/>}/>
                                    <Column className="text-right" header={translate("actions")} body={(row) => row.sessionStatus === "active" ? <Button icon="pi pi-sign-out" label={translate("user-revoke-session")} className="gap-2" text severity="danger" disabled={saving} onClick={() => openSecurityDialog("revoke", row)}/> : null}/>
                                </DataTable>
                            </div>
                        )}
                        {can(ADMIN_PERMISSIONS.USER_INTERNAL_NOTE) && (
                            <div>
                                <Button icon="pi pi-comment" label={translate("user-add-internal-note")} className="gap-2" outlined onClick={() => setNoteDialog(true)}/>
                            </div>
                        )}
                        <DataTable value={auditLogs} dataKey="documentId" paginator rows={10} emptyMessage={translate("datatable-empty-message")}>
                            <Column className="text-right" field="model" header={translate("audit-model")}/>
                            <Column className="text-right" field="action" header={translate("audit-action")}/>
                            <Column className="text-right ltr" field="performedById" header={translate("operations-actor")}/>
                            <Column className="text-right ltr" header={translate("createdAt")} body={(row) => dateText(row.createdAt)}/>
                        </DataTable>
                        <MarkdownHelp contentKey="user-tab-security-support-help"/>
                    </div>
                </TabPanel>
                )}
            </TabView>
            <Dialog
                visible={can(ADMIN_PERMISSIONS.USER_INTERNAL_NOTE) && noteDialog}
                onHide={() => !saving && setNoteDialog(false)}
                header={(
                    <div className="flex flex-column">
                        <span className="text-lg">{translate("user-add-internal-note")}</span>
                        <span className="text-xs text-400">{entityLabel(user)}</span>
                    </div>
                )}
                footer={(
                    <div className="flex flex-row-reverse align-items-center gap-2 justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300">
                        <Button icon="pi pi-save" label={translate("save")} className="text-xs gap-2 border-round-lg p-2" loading={saving} disabled={note.trim().length < 3} onClick={saveNote}/>
                        <Button label={translate("cancel")} className="text-xs p-button-secondary p-button-outlined border-round-lg p-2" disabled={saving} onClick={() => setNoteDialog(false)}/>
                    </div>
                )}
                pt={{footer: () => ({className: "p-0 border-round-bottom-2xl"}), content: () => ({className: "p-4"})}}
                headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                className="rtl w-11 md:w-7 lg:w-5 border-round-bottom-2xl"
                modal
                draggable={false}
            >
                <div className="flex flex-column gap-3">
                    <Message severity="info" text={translate("user-internal-note-description")} className="text-xs"/>
                    <InputTextarea rows={6} value={note} onChange={(event) => setNote(event.target.value)}/>
                </div>
            </Dialog>
            <Dialog
                visible={Boolean(securityDialog)}
                onHide={() => !saving && setSecurityDialog(null)}
                header={translate(securityDialog?.mode === "recover" ? "user-recover-access" : "user-revoke-session")}
                footer={(
                    <div className="flex flex-row-reverse align-items-center gap-2 justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300">
                        <Button
                            icon="pi pi-shield"
                            label={translate("confirm")}
                            className="text-xs gap-2 border-round-lg p-2"
                            severity="danger"
                            loading={saving}
                            disabled={!securityForm.phoneVerified || securityForm.verificationNote.trim().length < 10 || (securityDialog?.mode === "recover" && securityForm.newPassword.length < 8)}
                            onClick={runSessionSecurityAction}
                        />
                        <Button label={translate("cancel")} className="text-xs p-button-secondary p-button-outlined border-round-lg p-2" disabled={saving} onClick={() => setSecurityDialog(null)}/>
                    </div>
                )}
                pt={{footer: () => ({className: "p-0 border-round-bottom-2xl"}), content: () => ({className: "p-4"})}}
                headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                className="rtl w-11 md:w-7 lg:w-5 border-round-bottom-2xl"
                modal
                draggable={false}
            >
                <div className="flex flex-column gap-3">
                    <Message severity="warn" text={translate(securityDialog?.mode === "recover" ? "user-recover-access-warning" : "user-revoke-session-warning")} className="text-xs"/>
                    {securityDialog?.mode === "recover" && (
                        <>
                            <div className="flex flex-column gap-2">
                                <label className="font-bold" htmlFor="support-original-session">{translate("user-original-session")}</label>
                                <Dropdown
                                    inputId="support-original-session"
                                    className="w-full"
                                    panelClassName="rtl"
                                    value={securityForm.keepSessionDocumentId}
                                    options={[{label: translate("user-end-all-sessions"), value: ""}, ...activeSessionOptions]}
                                    onChange={(event) => setSecurityForm((current) => ({...current, keepSessionDocumentId: event.value}))}
                                />
                            </div>
                            <div className="flex flex-column gap-2">
                                <label className="font-bold" htmlFor="support-new-password">{translate("new-password")}</label>
                                <Password inputId="support-new-password" className="w-full" inputClassName="w-full ltr" value={securityForm.newPassword} feedback={false} toggleMask onChange={(event) => setSecurityForm((current) => ({...current, newPassword: event.target.value}))}/>
                                <small className="text-xs text-500">{translate("user-support-password-description")}</small>
                            </div>
                        </>
                    )}
                    <div className="flex flex-column gap-2">
                        <label className="font-bold" htmlFor="support-verification-note">{translate("user-phone-verification-note")}</label>
                        <InputTextarea id="support-verification-note" rows={4} value={securityForm.verificationNote} onChange={(event) => setSecurityForm((current) => ({...current, verificationNote: event.target.value}))}/>
                        <small className="text-xs text-500">{translate("user-phone-verification-note-description")}</small>
                    </div>
                    <label className="p-3 border-1 border-200 border-round flex align-items-center gap-2">
                        <Checkbox checked={securityForm.phoneVerified} onChange={(event) => setSecurityForm((current) => ({...current, phoneVerified: Boolean(event.checked)}))}/>
                        <span>{translate("user-phone-verification-confirm")}</span>
                    </label>
                </div>
            </Dialog>
        </div>
    );
}

export default User;
